import {
  FormGroup,
  HTMLSelect,
  InputGroup,
  Intent,
  Tag,
} from "@blueprintjs/core";
import { StatusProxy } from "@focus/focusUI";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useUserController } from "../../hooks/useUserController";
import type { User } from "../../types/users";
import { sortAlphabetically } from "../../utils/sorting";
import { EnableDisableButton } from "./EnableDisableButton";
import { PasswordForm } from "./PasswordForm";
import { ResetPasswordButton } from "./ResetPasswordButton";
import { RestoreTotpButton } from "./RestoreTotpButton";

export interface ExtendedUser extends User {
  password?: string;
  confirmPassword?: string;
}

interface UserAccessFormProps {
  content: User | ExtendedUser;
  onUpdate: (data: User) => void;
  onValidityChange?: (isValid: boolean) => void;
  readOnly?: boolean;
  isSelf?: boolean;
}

const FormSection = ({
  title,
  eyebrow,
  children,
  rightContent,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
  rightContent?: ReactNode;
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-xs font-semibold uppercase text-slate-500">
            {eyebrow}
          </div>
        )}
        <h3 className="mt-1 text-base font-semibold text-slate-950">
          {title}
        </h3>
      </div>
      {rightContent}
    </header>
    {children}
  </section>
);

const EmptyValue = () => (
  <span className="text-sm font-semibold text-slate-400">-</span>
);

const TokenList = ({
  items,
  readOnly,
  onRemove,
}: {
  items: string[];
  readOnly: boolean;
  onRemove: (index: number) => void;
}) => (
  <div className="mt-3 flex min-h-9 flex-wrap items-center gap-2">
    {items.length === 0 && <EmptyValue />}

    {items.map((item, index) => (
      <Tag
        className="!bg-slate-100 !px-2.5 !py-1 !text-slate-700"
        interactive={false}
        key={item}
        large
        minimal={readOnly}
        onRemove={readOnly ? undefined : () => onRemove(index)}
      >
        {item}
      </Tag>
    ))}
  </div>
);

export const UserForm = ({
  content,
  onUpdate,
  onValidityChange,
  readOnly = true,
  isSelf,
}: UserAccessFormProps) => {
  const {
    getUserInfo,
    organizations,
    roleGroups,
    getRequestStatus,
    getRequestError,
  } = useUserController();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sortedOrganizations = useMemo(
    () => sortAlphabetically(organizations),
    [organizations],
  );

  const validate = (data: ExtendedUser) => {
    const newErrors: Record<string, string> = {};

    if (!data.username || data.username.trim().length < 2) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!data.firstName || data.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 3 characters";
    }

    if (!data.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Invalid email address";
    }

    const shouldValidatePassword = "password" in data && !readOnly;

    if (shouldValidatePassword) {
      if (!data.password) {
        newErrors.password = "Password is required";
      } else {
        if (data.password.length < 10) {
          newErrors.password = "Password must be at least 10 characters";
        } else if (!/[A-Z]/.test(data.password)) {
          newErrors.password =
            "Password must include at least one uppercase letter";
        } else if (!/[a-z]/.test(data.password)) {
          newErrors.password =
            "Password must include at least one lowercase letter";
        } else if (!/[!@#$%^&*()_+~`|}{[\]:;?><,./\-=/]/.test(data.password)) {
          newErrors.password = "Password must include at least one symbol";
        }
      }

      if (!data.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (data.password !== data.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (data.organizations.length < 1) {
      newErrors.organizations = "At least an organization is required";
    }

    return newErrors;
  };

  const updateFields = (patch: Partial<ExtendedUser>) => {
    const newContent = { ...content, ...patch };
    onUpdate(newContent);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    updateFields({ [name]: value });
  };

  const removeFromArray = (
    field: "organizations" | "roleGroups",
    index: number,
  ) => {
    const updated = (content[field] as string[]).filter((_, i) => i !== index);
    const newContent = { ...content, [field]: updated };
    onUpdate(newContent);
  };

  useEffect(() => {
    if (content.id) {
      getUserInfo(content.id);
    }
  }, [content.id]);

  useEffect(() => {
    const errs = validate(content);
    setErrors(errs);
    onValidityChange?.(Object.keys(errs).length === 0);
  }, [content]);

  return (
    <StatusProxy
      status={getRequestStatus("getUser")}
      error={getRequestError("getUser")}
    >
      <div className="user-access-form max-h-[72vh] w-[720px] max-w-[calc(100vw-4rem)] overflow-y-auto pr-1">
        <div className="flex flex-col gap-4">
          {!readOnly && content?.id && (
            <FormSection
              eyebrow="User actions"
              rightContent={
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ring-1 ring-inset ${
                    content.enabled
                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                      : "bg-slate-100 text-slate-600 ring-slate-200"
                  }`}
                >
                  {content.enabled ? "Enabled" : "Disabled"}
                </span>
              }
              title="Security"
            >
              <div className="flex flex-wrap gap-2">
                <ResetPasswordButton
                  user={content}
                  text="Reset password"
                  intent={Intent.WARNING}
                />

                <RestoreTotpButton
                  user={content}
                  text="Restore TOTP"
                  intent={Intent.WARNING}
                />

                <EnableDisableButton
                  user={content}
                  text={content.enabled ? "Disable user" : "Enable user"}
                  intent={content.enabled ? Intent.DANGER : Intent.SUCCESS}
                  disabled={isSelf}
                />
              </div>
            </FormSection>
          )}

          {"password" in content && "confirmPassword" in content && (
            <PasswordForm
              content={content}
              onChange={updateFields}
              errors={errors}
            />
          )}

          <FormSection
            eyebrow={readOnly ? "Read only" : "Editable"}
            rightContent={
              content?.id ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
                  {content.id}
                </span>
              ) : undefined
            }
            title="Profile"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormGroup
                helperText={errors.username}
                intent={errors.username ? Intent.DANGER : Intent.NONE}
                label="Username"
                labelFor="username-input"
              >
                <InputGroup
                  id="username-input"
                  intent={errors.username ? Intent.DANGER : Intent.NONE}
                  name="username"
                  onChange={handleInputChange}
                  placeholder="Username"
                  readOnly={readOnly}
                  value={content?.username || ""}
                />
              </FormGroup>

              <FormGroup
                helperText={errors.email}
                intent={errors.email ? Intent.DANGER : Intent.NONE}
                label="Email"
                labelFor="email-input"
              >
                <InputGroup
                  id="email-input"
                  intent={errors.email ? Intent.DANGER : Intent.NONE}
                  name="email"
                  onChange={handleInputChange}
                  placeholder="Email"
                  readOnly={readOnly}
                  value={content?.email || ""}
                />
              </FormGroup>

              <FormGroup
                helperText={errors.firstName}
                intent={errors.firstName ? Intent.DANGER : Intent.NONE}
                label="First name"
                labelFor="firstname-input"
              >
                <InputGroup
                  id="firstname-input"
                  intent={errors.firstName ? Intent.DANGER : Intent.NONE}
                  name="firstName"
                  onChange={handleInputChange}
                  placeholder="First name"
                  readOnly={readOnly}
                  value={content?.firstName || ""}
                />
              </FormGroup>

              <FormGroup label="Last name" labelFor="lastname-input">
                <InputGroup
                  id="lastname-input"
                  name="lastName"
                  onChange={handleInputChange}
                  placeholder="Last name"
                  readOnly={readOnly}
                  value={content?.lastName || ""}
                />
              </FormGroup>
            </div>
          </FormSection>

          <FormSection eyebrow="Organizations and roles" title="Access">
            <div className="grid gap-4 md:grid-cols-2">
              <FormGroup
                helperText={errors.organizations}
                intent={errors.organizations ? Intent.DANGER : Intent.NONE}
                label="Organizations"
              >
                {!readOnly && (
                  <HTMLSelect
                    className="w-full"
                    fill
                    onChange={(e) => {
                      const selected = e.target.value;
                      if (
                        selected &&
                        !content?.organizations.includes(selected)
                      ) {
                        updateFields({
                          organizations: [
                            ...(content?.organizations ?? []),
                            selected,
                          ],
                        });
                      }
                    }}
                    options={[
                      {
                        label: "- Select organization -",
                        value: "",
                        disabled: true,
                      },
                      ...sortedOrganizations.map((o) => ({
                        label: o,
                        value: o,
                      })),
                    ]}
                    value=""
                  />
                )}

                <TokenList
                  items={content?.organizations ?? []}
                  onRemove={(index) => removeFromArray("organizations", index)}
                  readOnly={readOnly}
                />
              </FormGroup>

              <FormGroup label="Role groups">
                {!readOnly && (
                  <HTMLSelect
                    className="w-full"
                    fill
                    onChange={(e) => {
                      const selected = e.target.value;
                      if (selected && !content?.roleGroups.includes(selected)) {
                        updateFields({
                          roleGroups: [
                            ...(content?.roleGroups ?? []),
                            selected,
                          ],
                        });
                      }
                    }}
                    options={[
                      {
                        label: "- Select role group -",
                        value: "",
                        disabled: true,
                      },
                      ...roleGroups.map((g) => ({ label: g, value: g })),
                    ]}
                    value=""
                  />
                )}

                <TokenList
                  items={content?.roleGroups ?? []}
                  onRemove={(index) => removeFromArray("roleGroups", index)}
                  readOnly={readOnly}
                />
              </FormGroup>
            </div>
          </FormSection>
        </div>
      </div>
    </StatusProxy>
  );
};
